import { useEffect, useMemo, useRef, useState } from "react"
import type { AgGridReact } from "ag-grid-react"
import type { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community"
import { AnimatePresence } from "motion/react"
import {
  FunnelSimple,
  Package,
  PencilSimple,
  TrendDown,
  TrendUp,
  Warning,
} from "@phosphor-icons/react"

import { Badge, Button, IconButton, ItemIdentity } from "~/components/design-system/components"
import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import FilterPopover from "~/components/planning/FilterPopover"
import { FilterRowAnimated } from "~/components/planning/ViewConfiguration"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import {
  DialogContentV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
} from "~/components/reusable/Dialog"
import { useFilters } from "~/hooks/useFilters"
import {
  FilterFieldsOverrideContext,
  type FilterFieldsOverride,
} from "~/hooks/useFilterFieldsOverride"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import { api, type RouterOutputs } from "~/utils/api"
import type { Filter, FilterFieldType } from "~/utils/filter"
import { cn } from "~/utils/helpers"
import {
  LINE_SEARCH_OPTIONS,
  matchesLineSearch,
  toLineSearchField,
  type LineSearchField,
  type LineSearchRow,
} from "~/utils/lineSearch"
import {
  REFERENCE_ROS_LEVEL_LABELS,
  parseReferenceRosLevel,
  type ReferenceRosLevel,
} from "~/utils/rateOfSale"
import { filterRowsById } from "../AllocationFilters"
import { distinctValues } from "~/utils/tsUtils"
import { formatRate } from "../format"
import type { toRateOfSaleInput } from "./setupDrafts"

export function linesLabel(count: number): string {
  return `${count.toLocaleString()} ${count === 1 ? "line" : "lines"}`
}

type SettingsInput = ReturnType<typeof toRateOfSaleInput>
type PreviewLine = RouterOutputs["allocation"]["previewReferenceRos"][number]
type PreviewRow = PreviewLine & {
  source: string
  changed: boolean
  noRosBasis: boolean
  referenceZero: boolean
  ownAboveUsed: boolean
  noDaysWithStock: boolean
}

function toPreviewRow(line: PreviewLine, levels: ReferenceRosLevel[]): PreviewRow {
  return {
    ...line,
    source: sourceLabel(line.level, levels),
    changed: lineChanged(line),
    noRosBasis: line.level == null,
    referenceZero: line.level != null && line.referenceRateOfSale === 0,
    ownAboveUsed:
      line.ownRateOfSale != null && line.ownRateOfSale > (line.referenceRateOfSale ?? 0),
    noDaysWithStock: line.daysWithInventory === 0,
  }
}

function previewSearchRow(row: PreviewRow): LineSearchRow {
  return { ...row, skuIds: [row.skuId], sizes: [row.size] }
}

function rateOrDash(value: number | null | undefined): string {
  return value == null ? "–" : `${formatRate(value)} /day`
}

function lineChanged(line: PreviewLine): boolean {
  if (line.level !== line.currentLevel) return true
  if (line.referenceRateOfSale == null || line.currentRos == null) {
    return line.referenceRateOfSale !== line.currentRos
  }
  return Math.abs(line.referenceRateOfSale - line.currentRos) > 1e-9
}

function sourceLabel(level: ReferenceRosLevel | null, levels: ReferenceRosLevel[]): string {
  if (!level) return "No ROS basis"
  return `Level ${levels.indexOf(level) + 1} · ${REFERENCE_ROS_LEVEL_LABELS[level].title}`
}

/** Adds the source to the table's "Source is one of" filter, or takes it out if it is in. */
function toggleSourceFilter(filters: Filter[], source: string): Filter[] {
  const index = filters.findIndex(
    (filter) => filter.field === "source" && filter.operator === "oneOf",
  )
  if (index < 0) {
    return [
      ...filters,
      { field: "source", operator: "oneOf", value: "", values: [source], by: "id" },
    ]
  }
  const current = filters[index]!
  const values = current.values.includes(source)
    ? current.values.filter((value) => value !== source)
    : [...current.values, source]
  if (values.length === 0) return filters.filter((_, other) => other !== index)
  return filters.map((filter, other) => (other === index ? { ...filter, values } : filter))
}

function SourceCell(params: ICellRendererParams<PreviewRow, string>) {
  const { filters, setFilters } = useFilters()
  const source = params.value ?? ""
  const toggle = () => void setFilters(toggleSourceFilter(filters, source))
  return (
    <Badge
      variant={params.data?.level ? "tag" : "stockout"}
      size="sm"
      role="button"
      tabIndex={0}
      title="Filter on this source"
      className="max-w-full cursor-pointer overflow-hidden"
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return
        event.preventDefault()
        toggle()
      }}
    >
      {source}
    </Badge>
  )
}

const PREVIEW_TABLE_ID = "allocation-reference-ros-preview"
const PREVIEW_GRID_OPTIONS: GridOptions<PreviewRow> = { rowHeight: 26 }

function levelGroupName(row: PreviewRow, level: ReferenceRosLevel): string {
  const { group } = parseReferenceRosLevel(level)
  if (group === "variant") return row.variantName ?? row.productName
  if (group === "product") return row.productName
  return row.category ?? "the category"
}

function levelStores(level: ReferenceRosLevel): string {
  return parseReferenceRosLevel(level).sameStore ? "in this store" : "across all stores"
}

function levelSizes(row: PreviewRow, level: ReferenceRosLevel): string {
  if (!parseReferenceRosLevel(level).sameSize) return "all sizes"
  return row.size ? `size ${row.size}` : "the same size"
}

/** In plain words, what the line's ROS rests on. */
function referenceReason(row: PreviewRow): string {
  if (!row.level) {
    return "No level in the chain had enough lines with their own ROS, so this line has no ROS basis."
  }
  const lines = row.referenceRateOfSaleLines ?? 0
  const basis =
    lines === 1 ? "1 line with its own ROS" : `${lines.toLocaleString()} lines with their own ROS`
  const reason = `Average of ${basis} in ${levelGroupName(row, row.level)}, ${levelSizes(row, row.level)}, ${levelStores(row.level)}.`
  if (row.referenceRateOfSale === 0) {
    return `${reason} ${lines === 1 ? "It sold nothing" : "None of them sold anything"} in the window.`
  }
  return reason
}

function VariantCell(params: ICellRendererParams<PreviewRow, string>) {
  return (
    <ItemIdentity
      title={params.value}
      imageUrl={params.data?.imageUrl ?? null}
      imageSize={20}
      gapClassName="gap-2"
      className="w-full"
    />
  )
}

function previewColumns(): ColDef<PreviewRow>[] {
  return [
    {
      colId: "variant",
      headerName: "Variant",
      flex: 1,
      minWidth: 200,
      valueGetter: (params) =>
        [params.data?.productName, params.data?.variantName].filter(Boolean).join(" · "),
      cellRenderer: VariantCell,
      cellClass:
        "!flex items-center overflow-hidden [&_.ag-cell-value]:min-w-0 [&_.ag-cell-value]:flex-1 [&_.ag-cell-wrapper]:min-w-0 [&_.ag-cell-wrapper]:flex-1",
    },
    { field: "storeName", headerName: "Store", width: 150 },
    { field: "size", headerName: "Size", width: 70 },
    {
      field: "daysWithInventory",
      headerName: "Days w/ stock",
      headerTooltip: "Days with stock in the sales history window.",
      width: 110,
      cellClass: "tabular-nums",
    },
    {
      field: "ownRateOfSale",
      headerName: "Own ROS",
      headerTooltip:
        "What the line's own sales give per day. Not used, because it is below the minimum.",
      width: 100,
      valueFormatter: (params) => rateOrDash(params.value),
      cellClass: "tabular-nums text-fg-muted line-through",
    },
    {
      field: "source",
      headerName: "Source",
      width: 300,
      cellRenderer: SourceCell,
      cellClass: "!flex items-center",
    },
    {
      field: "referenceRateOfSale",
      headerName: "ROS used",
      headerTooltip: "The ROS the line runs on with these settings.",
      width: 100,
      cellClass: "tabular-nums",
      tooltip: (params) => (params.data ? referenceReason(params.data) : undefined),
      valueFormatter: (params) => rateOrDash(params.value),
    },
    {
      field: "referenceRateOfSaleLines",
      headerName: "Based on",
      headerTooltip: "How many lines with their own ROS the ROS used is averaged from.",
      width: 100,
      cellClass: "tabular-nums",
      valueFormatter: (params) => (params.value == null ? "–" : linesLabel(params.value)),
      tooltip: (params) => (params.data ? referenceReason(params.data) : undefined),
    },
    {
      field: "currentRos",
      headerName: "Now",
      headerTooltip: "The ROS the line runs on with the saved settings.",
      width: 100,
      valueFormatter: (params) => rateOrDash(params.value),
      cellClass: (params) =>
        cn("tabular-nums", params.data?.changed ? "text-fg-base" : "text-fg-muted"),
    },
  ]
}

const PREVIEW_STEPS = [
  "Finding lines with too little history",
  "Trying the Reference ROS levels in order",
  "Picking the ROS for each line",
]

function PreviewLoader({ levels }: { levels: number }) {
  const [step, setStep] = useState(0)
  useEffect(() => {
    const timer = setInterval(
      () => setStep((current) => Math.min(current + 1, PREVIEW_STEPS.length - 1)),
      1500,
    )
    return () => clearInterval(timer)
  }, [])

  return (
    <div className="flex flex-col items-center gap-3.5 px-6 py-6">
      <div className="flex h-8.5 items-end gap-1.25" aria-hidden>
        {[0, 1, 2, 3, 4, 5, 6].map((bar) => (
          <span
            key={bar}
            className={cn(
              "h-8.5 w-1.75 origin-bottom animate-loader-bar rounded-sm",
              bar % 2 ? "bg-button-primary" : "bg-badge-blue-bg",
            )}
            style={{ animationDelay: `${bar * 0.12}s` }}
          />
        ))}
      </div>
      <DialogTitle className="paragraph-base font-semibold text-fg-base">
        Calculating ROS with these settings
      </DialogTitle>
      <span className="paragraph-sm text-fg-muted">
        {step === 1 ? `Trying ${levels} Reference ROS levels in order` : PREVIEW_STEPS[step]}
      </span>
    </div>
  )
}

const PREVIEW_FILTER_FIELDS: FilterFieldType[] = [
  { value: "productName", name: "Product name", type: "string" },
  { value: "variantName", name: "Variant name", type: "string" },
  { value: "size", name: "Size", type: "string" },
  { value: "storeName", name: "Store", type: "string" },
  { value: "source", name: "Source", type: "string" },
  { value: "daysWithInventory", name: "Days with stock", type: "number" },
  { value: "ownRateOfSale", name: "Own ROS", type: "number" },
  { value: "referenceRateOfSale", name: "ROS used", type: "number" },
  { value: "currentRos", name: "Now", type: "number" },
  { value: "noRosBasis", name: "No ROS basis", type: "boolean", icon: <Warning /> },
  { value: "referenceZero", name: "Reference ROS of 0", type: "boolean", icon: <TrendDown /> },
  { value: "ownAboveUsed", name: "Own ROS above ROS used", type: "boolean", icon: <TrendUp /> },
  { value: "noDaysWithStock", name: "No days with stock", type: "boolean", icon: <Package /> },
]

const CHANGED_FILTER_FIELD: FilterFieldType = {
  value: "changed",
  name: "Changed by these settings",
  type: "boolean",
  icon: <PencilSimple />,
}

/** The lines below the minimum as saving these settings would build them, next to today's ROS. */
export function ReferenceRosPreviewDialog({
  open,
  onOpenChange,
  input,
  edited,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  input: SettingsInput
  edited: boolean
}) {
  const preview = api.allocation.previewReferenceRos.useQuery(input, {
    enabled: open,
    staleTime: Infinity,
  })

  let content = <PreviewLoader levels={input.referenceRosLevels.length} />
  if (preview.data) {
    content = (
      <FilterScopeProvider scopeKey="allocation-reference-ros-preview">
        <PreviewResults lines={preview.data} input={input} edited={edited} />
      </FilterScopeProvider>
    )
  } else if (preview.isError) {
    content = (
      <div className="flex flex-col items-center gap-3 px-6 py-6 text-center">
        <DialogTitle className="paragraph-base font-semibold text-fg-base">
          The preview could not be calculated
        </DialogTitle>
        {preview.error.data?.code === "BAD_REQUEST" ? (
          <span className="paragraph-sm text-fg-muted">
            These settings cannot be saved yet, so there is nothing to preview. Fix them first.
          </span>
        ) : (
          <>
            <span className="paragraph-sm text-fg-muted">
              Something went wrong while calculating the lines.
            </span>
            <Button variant="neutral" size="sm" onClick={() => void preview.refetch()}>
              Try again
            </Button>
          </>
        )}
      </div>
    )
  }

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContentV2
        className={cn(preview.data ? "flex h-[85vh] max-w-6xl flex-col" : "max-w-sm")}
      >
        {content}
      </DialogContentV2>
    </DialogRoot>
  )
}

/** Lines per source in chain order, then the lines no level covers. Each one toggles the Source
 * filter, like the pills in the table. */
function SourceCounts({ rows, levels }: { rows: PreviewRow[]; levels: ReferenceRosLevel[] }) {
  const { filters, setFilters } = useFilters()
  const selected = new Set(
    filters.find((filter) => filter.field === "source" && filter.operator === "oneOf")?.values,
  )
  const counts = new Map<string, number>()
  for (const row of rows) counts.set(row.source, (counts.get(row.source) ?? 0) + 1)
  const sources = [...levels.map((level) => sourceLabel(level, levels)), sourceLabel(null, levels)]

  return (
    <div className="flex flex-wrap gap-1 border-b border-base px-3 py-2">
      {sources.map((source) => {
        const isSelected = selected.has(source)
        return (
          <Button
            key={source}
            variant="transparent"
            size="sm"
            title="Filter on this source"
            onClick={() => void setFilters(toggleSourceFilter(filters, source))}
            className="group relative h-auto px-2 py-1 hover:bg-transparent active:bg-transparent [&>div]:items-start"
          >
            <div className="flex flex-col text-left">
              <span
                className={cn(
                  "label-sm",
                  isSelected ? "text-fg-interactive" : "text-fg-muted group-hover:text-fg-base",
                )}
              >
                {source}
              </span>
              <span
                className={cn(
                  "heading-xs mt-0.5 tabular-nums",
                  isSelected && "text-fg-interactive",
                )}
              >
                {(counts.get(source) ?? 0).toLocaleString()}
              </span>
            </div>
            <span
              className={cn(
                "absolute inset-x-2 -bottom-2 h-0.5 rounded-full",
                isSelected
                  ? "bg-button-primary"
                  : "border-b-2 border-strong opacity-0 group-hover:opacity-100",
              )}
            />
          </Button>
        )
      })}
    </div>
  )
}

function PreviewResults({
  lines,
  input,
  edited,
}: {
  lines: PreviewLine[]
  input: SettingsInput
  edited: boolean
}) {
  const gridRef = useRef<AgGridReact<PreviewRow>>(null)
  const [search, setSearch] = useState("")
  const [searchBy, setSearchBy] = useState<LineSearchField>("all")
  const { filters, setFilters } = useFilters()
  const levels = input.referenceRosLevels
  const rows = useMemo(
    () =>
      lines
        .map((line) => toPreviewRow(line, levels))
        .sort((a, b) => Number(b.changed) - Number(a.changed)),
    [lines, levels],
  )
  const columns = useMemo(() => previewColumns(), [])
  const fields = useMemo(
    () => (edited ? [...PREVIEW_FILTER_FIELDS, CHANGED_FILTER_FIELD] : PREVIEW_FILTER_FIELDS),
    [edited],
  )
  const filterFields = useMemo<FilterFieldsOverride>(
    () => ({
      fields,
      optionsByField: {
        productName: distinctValues(rows.map((row) => row.productName)),
        variantName: distinctValues(rows.map((row) => row.variantName)),
        size: distinctValues(rows.map((row) => row.size)),
        storeName: distinctValues(rows.map((row) => row.storeName)),
        source: distinctValues(rows.map((row) => row.source)),
      },
    }),
    [rows, fields],
  )
  const shownRows = useMemo(
    () =>
      filterRowsById(
        rows.filter((row) => matchesLineSearch(row, search, searchBy, previewSearchRow)),
        filters,
        (field) => fields.find((option) => option.value === field)?.type,
      ),
    [rows, search, searchBy, filters, fields],
  )
  const changed = rows.filter((row) => row.changed).length

  return (
    <FilterFieldsOverrideContext.Provider value={filterFields}>
      <DialogHeaderV2 className="gap-1.5">
        <div className="flex flex-wrap items-center gap-2 pr-10">
          <DialogTitle className="paragraph-base font-semibold text-fg-base">
            SKU lines on Reference ROS
          </DialogTitle>
          <Badge variant="draft">{linesLabel(rows.length)}</Badge>
          {edited && <Badge variant="tag">{`${changed.toLocaleString()} changed`}</Badge>}
          <div className="flex-1" />
          <ExpandableSearchField
            initialSearchText={search}
            onSearchTextChange={setSearch}
            placeholder="Search..."
            debounceTime={200}
            searchBy={searchBy}
            onSearchByChange={(field) => setSearchBy(toLineSearchField(field))}
            searchByOptions={LINE_SEARCH_OPTIONS}
          />
          <FilterPopover
            align="end"
            customTrigger={
              <IconButton variant="neutral" size="sm" aria-label="Add filter">
                <FunnelSimple />
              </IconButton>
            }
          />
        </div>
        <span className="paragraph-xs text-fg-muted">
          {`Lines with fewer than ${input.minDaysWithStock} days with stock.${edited ? " Calculated with your unsaved settings; nothing is saved. Changed lines come first." : ""}`}
        </span>
      </DialogHeaderV2>
      <SourceCounts rows={rows} levels={levels} />
      <AnimatePresence initial={false}>
        {filters.length > 0 && (
          <FilterRowAnimated
            filters={filters}
            supportsViews={false}
            isCurrentViewChanged={false}
            hasCurrentView={false}
            showClearView={false}
            backToDefault={() => void setFilters([])}
          />
        )}
      </AnimatePresence>
      <div className="min-h-0 flex-1 px-5 py-4">
        <DataTableBaseV2<PreviewRow>
          id={PREVIEW_TABLE_ID}
          gridRef={gridRef}
          rowData={shownRows}
          rowIdField="id"
          columnDefs={columns}
          defaultColumnDef={{
            sortable: true,
            resizable: true,
            minWidth: 60,
            suppressHeaderMenuButton: true,
          }}
          emptyMessage={
            rows.length === 0
              ? "No SKU lines are on Reference ROS."
              : "No lines match the current filters."
          }
          gridOptions={PREVIEW_GRID_OPTIONS}
          disableStatePersistence
          compact
        />
      </div>
    </FilterFieldsOverrideContext.Provider>
  )
}
